<!--
  File name: PBNEForm.vue
  Program Description:
    - This component contains the action buttons that can be performed in each
      PBNEF file
    - Each action button have display restrictions based on the user roles/s of
      the logged in user
    - This component fetch the input fields needed for PBNEF and display for user
      to see and update
    - This file also contains the logic for saving PBNEF

  Includes:
    - PBNEFBreadCrumbs
    - PBNEFDefaults: values from PDBNF
    - PBNEFInputs: all PBNEF input fields
    - ActionModal: used for the modal containing the dropdown selection when
      endorsing a PBNEF to adjudication recipient
    - Validations per field before saving
    - Function that change the PDBNF status based on the PBNEF status
    _ Payload schema for saving PBNEF
-->
<template>
  <v-container>
    <PBNEFBreadCrumbs />
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col class="mb-0 pb-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <v-card-title class="pt-2 pb-2">
            <!-- For all user role -->
            <v-btn
              v-if="operation === 'update' || operation === 'create'"
              color="grey darken-1"
              text
              :loading="$meta.loading"
              @click="$store.dispatch('showDialog', {
                title: 'Dicard Changes',
                message: 'Changes will not be saved. Are you sure you want to discard?',
                buttonLabel: 'Discard',
                buttonColor: '#A22929',
                callback: () => { $router.push({ name: 'PBNEFLanding' }) }
              })"
            >
              Cancel
            </v-btn>
            <!-- For evaluator -->
            <v-btn
              v-else-if="operation === 'view' && !isSubmitted && isEvaluator && forInitialization"
              id="back-btn"
              color="grey darken-1"
              text
              :loading="$meta.loading"
              @click="$router.push({
                name: 'PBNEFDrafts'
              })"
            >
              <v-icon>mdi-arrow-left-thin</v-icon> Back to Drafts
            </v-btn>
            <!-- For all user role -->
            <v-btn
              v-else
              id="back-btn"
              color="grey darken-1"
              text
              :loading="$meta.loading"
              @click="$router.push({
                name: 'PBNEFLanding'
              })"
            >
              <v-icon>mdi-arrow-left-thin</v-icon> Back to {{ dbnNumber }}
            </v-btn>
            <v-spacer />
            <template v-if="isPdbnfValid">
              <!-- For evaluator - Initial -->
              <v-btn
                v-if="
                  showLabel('canPostPbnef') &&
                    (operation && operation !== 'view') &&
                    typeOfRequests.length &&
                    isEvaluator &&
                    forInitialization &&
                    isAssignee
                "
                id="save-as-draft-btn"
                text
                color="primary"
                :loading="$meta.loading"
                @click="confirmDialog('draft', save)"
              >
                {{ getLabel(`canPostPbnef`, 'Save as Draft') }}
              </v-btn>
              <!-- For evaluator - DRAFTS -->
              <v-btn
                v-if="
                  showLabel('canPatchPbnef') &&
                    (operation && operation === 'view') &&
                    !isSubmitted &&
                    isEvaluator &&
                    forInitialization &&
                    isAssignee
                "
                id="edit-draft-btn"
                color="primary"
                type="button"
                :loading="$meta.loading"
                @click="$router.replace({
                  name: 'PBNEFView',
                  params: {
                    operation: 'update',
                    id
                  }
                })"
              >
                <v-icon
                  small
                  class="mr-2"
                >
                  mdi-pencil
                </v-icon>
                {{ getLabel(`canPatchPbnef`, 'Edit Draft') }}
              </v-btn>

              <!-- For evaluator -->
              <template
                v-if="
                  showLabel('canPostPbnef') &&
                    (operation && operation !== 'view') &&
                    typeOfRequests.length &&
                    isEvaluator &&
                    forInitialization &&
                    isAssignee
                "
              >
                <v-btn
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="$meta.loading"
                  @click="confirmDialog('save', save)"
                >
                  {{ getLabel(`canPostPbnef`, 'Submit PBNEF') }}
                </v-btn>
              </template>

              <!-- For First Reviewer & Final reviewer -->
              <template
                v-if="
                  showLabel('canPatchPbnef') &&
                    (operation && operation === 'view') &&
                    (isAdjudicator || isReviewer) &&
                    (forFirstReview || forFinalReview || (forApproval && notYetApproved)) &&
                    isAssignee &&
                    isStageValid
                "
              >
                <v-btn
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="$meta.loading"
                  @click="$router.replace({
                    params: {
                      operation: 'update'
                    }
                  })"
                >
                  <v-icon
                    small
                    class="mr-2"
                  >
                    mdi-pencil
                  </v-icon>
                  {{ getLabel(`canPatchPbnef`, `${isAdjudicator ? 'Update PBNEF' : 'Update'}`) }}
                </v-btn>
              </template>
              <template
                v-if="
                  showLabel('canPatchPbnef') &&
                    (operation && operation !== 'view') &&
                    isReviewer &&
                    forFirstReview &&
                    isAssignee
                "
              >
                <v-btn
                  id="submit-btn"
                  color="primary"
                  type="button"
                  :loading="$meta.loading"
                  @click="confirmDialog('update', save)"
                >
                  {{ getLabel(`canPatchPbnef`, 'Update PBNEF') }}
                </v-btn>
              </template>

              <!-- For Final Reviewer -->
              <template
                v-if="(operation && operation !== 'view') && forFinalReview && isReviewer && isAssignee"
              >
                <v-btn
                  id="pbnef-btn"
                  color="primary"
                  text
                  class="white--text ml-8 mr-4 btn"
                  @click="endorseConfirmation"
                >
                  <v-icon
                    small
                    color="primary"
                  >
                    mdi-swap-horizontal
                  </v-icon>
                  Endorse
                </v-btn>
              </template>
              <!-- For Final Reviewer and Adjudication Recipient-->
              <div
                v-if="
                  (operation && operation !== 'view') &&
                    isAssignee &&
                    (isAdjudicator || isReviewer) &&
                    ((forFinalReview && !withEndorsement) || (forApproval && withEndorsement)) &&
                    notYetApproved
                "
                style="flex-direcion: row"
              >
                <v-btn
                  v-if="evaluation === 'Granted'"
                  id="pbnef-btn"
                  color="#218100"
                  class="white--text ml-8 mr-4 btn"
                  @click="confirmApproval('approve')"
                >
                  <v-icon
                    small
                    color="white"
                    class="mr-2"
                  >
                    mdi-check
                  </v-icon>
                  Approve Main Request
                </v-btn>
                <v-btn
                  v-else
                  id="pbnef-btn"
                  color="#A22929"
                  class="white--text mx-4 btn"
                  @click="confirmApproval('reject')"
                >
                  <v-icon
                    small
                    color="white"
                    class="mr-2"
                  >
                    mdi-close
                  </v-icon>
                  Reject Main Request
                </v-btn>
              </div>
            </template>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row
      align-self="start"
      justify="center"
    >
      <v-col class="mt-0 pt-0">
        <v-card
          :key="operation"
          tile
          elevation="0"
          outlined
        >
          <v-card-text class="pt-0">
            <v-container>
              <v-row>
                <v-col class="px-6 py-4">
                  <h1 class="title primary--text">
                    Preliminary Breach Notification Evaluation Form
                  </h1>
                </v-col>
              </v-row>
              <v-container>
                <PBNEFDefaults />
                <PBNEFInputs
                  ref="form"
                />
              </v-container>
            </v-container>
          </v-card-text>
          <v-row
            align-self="start"
            justify="center"
          >
            <v-col class="mb-0 pb-0">
              <v-card
                :key="operation"
                tile
                elevation="0"
                outlined
              >
                <v-card-title class="pt-2 pb-2">
                  <!-- For all user role -->
                  <v-btn
                    v-if="operation === 'update' || operation === 'create'"
                    color="grey darken-1"
                    text
                    :loading="$meta.loading"
                    @click="$store.dispatch('showDialog', {
                      title: 'Dicard Changes',
                      message: 'Changes will not be saved. Are you sure you want to discard?',
                      buttonLabel: 'Discard',
                      buttonColor: '#A22929',
                      callback: () => { $router.push({ name: 'PBNEFLanding' }) }
                    })"
                  >
                    Cancel
                  </v-btn>
                  <!-- For evaluator -->
                  <v-btn
                    v-else-if="operation === 'view' && !isSubmitted && isEvaluator && forInitialization"
                    id="back-btn"
                    color="grey darken-1"
                    text
                    :loading="$meta.loading"
                    @click="$router.push({
                      name: 'PBNEFDrafts'
                    })"
                  >
                    <v-icon>mdi-arrow-left-thin</v-icon> Back to Drafts
                  </v-btn>
                  <!-- For all user role -->
                  <v-btn
                    v-else
                    id="back-btn"
                    color="grey darken-1"
                    text
                    :loading="$meta.loading"
                    @click="$router.push({
                      name: 'PBNEFLanding'
                    })"
                  >
                    <v-icon>mdi-arrow-left-thin</v-icon> Back to {{ dbnNumber }}
                  </v-btn>
                  <v-spacer />

                  <template v-if="isPdbnfValid">
                    <!-- For evaluator - Initial -->
                    <v-btn
                      v-if="
                        showLabel('canPostPbnef') &&
                          (operation && operation !== 'view') &&
                          typeOfRequests.length &&
                          isEvaluator &&
                          forInitialization &&
                          isAssignee
                      "
                      id="save-as-draft-btn"
                      text
                      color="primary"
                      :loading="$meta.loading"
                      @click="confirmDialog('draft', save)"
                    >
                      {{ getLabel(`canPostPbnef`, 'Save as Draft') }}
                    </v-btn>
                    <!-- For evaluator - DRAFTS -->
                    <v-btn
                      v-if="
                        showLabel('canPatchPbnef') &&
                          (operation && operation === 'view') &&
                          !isSubmitted &&
                          isEvaluator &&
                          forInitialization &&
                          isAssignee
                      "
                      id="edit-draft-btn"
                      color="primary"
                      type="button"
                      :loading="$meta.loading"
                      @click="$router.replace({
                        name: 'PBNEFView',
                        params: {
                          operation: 'update',
                          id
                        }
                      })"
                    >
                      <v-icon
                        small
                        class="mr-2"
                      >
                        mdi-pencil
                      </v-icon>
                      {{ getLabel(`canPatchPbnef`, 'Edit Draft') }}
                    </v-btn>

                    <!-- For evaluator -->
                    <template
                      v-if="
                        showLabel('canPostPbnef') &&
                          (operation && operation !== 'view') &&
                          typeOfRequests.length &&
                          isEvaluator &&
                          forInitialization &&
                          isAssignee
                      "
                    >
                      <v-btn
                        id="submit-btn"
                        color="primary"
                        type="button"
                        :loading="$meta.loading"
                        @click="confirmDialog('save', save)"
                      >
                        {{ getLabel(`canPostPbnef`, 'Submit PBNEF') }}
                      </v-btn>
                    </template>

                    <!-- For First Reviewer & Final reviewer -->
                    <template
                      v-if="
                        showLabel('canPatchPbnef') &&
                          (operation && operation === 'view') &&
                          (isAdjudicator || isReviewer) &&
                          (forFirstReview || forFinalReview || (forApproval && notYetApproved)) &&
                          isAssignee &&
                          isStageValid
                      "
                    >
                      <v-btn
                        id="submit-btn"
                        color="primary"
                        type="button"
                        :loading="$meta.loading"
                        @click="$router.replace({
                          params: {
                            operation: 'update'
                          }
                        })"
                      >
                        <v-icon
                          small
                          class="mr-2"
                        >
                          mdi-pencil
                        </v-icon>
                        {{ getLabel(`canPatchPbnef`, `${isAdjudicator ? 'Update PBNEF' : 'Update'}`) }}
                      </v-btn>
                    </template>
                    <template
                      v-if="
                        showLabel('canPatchPbnef') &&
                          (operation && operation !== 'view') &&
                          isReviewer &&
                          forFirstReview &&
                          isAssignee
                      "
                    >
                      <v-btn
                        id="submit-btn"
                        color="primary"
                        type="button"
                        :loading="$meta.loading"
                        @click="confirmDialog('update', save)"
                      >
                        {{ getLabel(`canPatchPbnef`, 'Update PBNEF') }}
                      </v-btn>
                    </template>

                    <!-- For Final Reviewer -->
                    <template
                      v-if="(operation && operation !== 'view') && forFinalReview && isReviewer && isAssignee"
                    >
                      <v-btn
                        id="pbnef-btn"
                        color="primary"
                        text
                        class="white--text ml-8 mr-4 btn"
                        @click="endorseConfirmation"
                      >
                        <v-icon
                          small
                          color="primary"
                        >
                          mdi-swap-horizontal
                        </v-icon>
                        Endorse
                      </v-btn>
                    </template>
                    <!-- For Final Reviewer and Adjudication Recipient-->
                    <div
                      v-if="
                        (operation && operation !== 'view') &&
                          isAssignee &&
                          (isAdjudicator || isReviewer) &&
                          ((forFinalReview && !withEndorsement) || (forApproval && withEndorsement)) &&
                          notYetApproved
                      "
                      style="flex-direcion: row"
                    >
                      <v-btn
                        v-if="evaluation === 'Granted'"
                        id="pbnef-btn"
                        color="#218100"
                        class="white--text ml-8 mr-4 btn"
                        @click="confirmApproval('approve')"
                      >
                        <v-icon
                          small
                          color="white"
                          class="mr-2"
                        >
                          mdi-check
                        </v-icon>
                        Approve Main Request
                      </v-btn>
                      <v-btn
                        v-else
                        id="pbnef-btn"
                        color="#A22929"
                        class="white--text mx-4 btn"
                        @click="confirmApproval('reject')"
                      >
                        <v-icon
                          small
                          color="white"
                          class="mr-2"
                        >
                          mdi-close
                        </v-icon>
                        Reject Main Request
                      </v-btn>
                    </div>
                    <v-spacer style="max-width: 3%" />
                  </template>
                </v-card-title>
              </v-card>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
    <ActionModal
      v-if="isForEndorsement"
      v-model="endorsedTo"
      title="Endorse PBNEF to Adjudication"
      child="VAutocomplete"
      button-label="Endorse"
      button-color="primary darken-1"
      :v-bind="{
        label: 'Endorse To',
        name: 'endorsedTo',
        value: 'endorsedTo',
        'item-text': 'fullName',
        'item-value': '_id',
        outlined: true,
        items: users.filter(x => x.userRole.includes('adjudicationRecipient')),
        'error-messages': genericError('endorsedTo', 'Endorsed To')
      }"
      :dialog="isForEndorsement"
      @dialog="(val) => {
        isForEndorsement = val
        if (!val) endorsedTo = ''
      }"
      @callback="endorse"
    />
    <v-btn
      v-show="showFab"
      fab
      fixed
      bottom
      right
      class="btn--fab"
      color="#161E6D"
      @click="scrollToTop()"
    >
      <v-icon color="#FFF">
        mdi-arrow-up
      </v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { privilegeMixin } from 'session-plugin'
import { mapFields } from 'vuex-map-fields'
import { capitalize } from '@/utils/helpers'
import crud from '@/utils/rest/crud'
import pbnefMixin from '@/mixins/pbnefMixin'
import ActionModal from '@/components/local/ActionModal'

import PBNEFDefaults from './PBNEFDefaults.vue'
import PBNEFInputs from './PBNEFInputs.vue'
import PBNEFBreadCrumbs from './PBNEFBreadCrumbs.vue'

import { validationMixin, validationErrors, endorseValidation } from '@/utils/validations'

export default {
  name: 'PBNEFForm',
  components: {
    PBNEFDefaults,
    PBNEFInputs,
    PBNEFBreadCrumbs,
    ActionModal
  },
  mixins: [
    privilegeMixin,
    validationMixin,
    pbnefMixin
  ],
  data: () => ({
    showFab: false,
    isForEndorsement: false,
    actionStatus: '',
    maxFileSize: 50
  }),
  computed: {
    ...validationErrors,
    ...mapFields('pbnef', [
      '$meta',
      'dbnNumber',
      'isSubmitted',
      'status',
      'docketNumber',
      'requestFor',
      'specify',
      'dataSubjects',
      'specifyIfOthers',
      'specifyIfGroup',
      'numberOfDataSubjectsAffected',
      'atLeastOneHundredIndividuals',
      'natureOfPersonalDataInvolved',
      'measuresInPlace',
      'explanation',
      'evaluation',
      'dbnStatus',
      'typeOfRequests',
      'evaluatedBy',
      'initialReviewedBy',
      'finalReviewBy',
      'approvedBy',
      'approveReject',
      'endorsedTo',
      'forAdjudication',
      'users',
      'statusObject',
      'statusHistory',
      'fileUpload',
      'isStageValid',
      'pdbnfRequest'
    ]),
    id () {
      return this.$route.params.id
    },
    operation () {
      return this.$route.params.operation
    },
    dialogInfo () {
      return {
        save: {
          title: 'Submit PBNEF',
          message: `Are you sure you want to submit ${this.dbnNumber}'s PBNEF?`,
          buttonLabel: 'Submit',
          buttonColor: '#218100'
        },
        update: {
          title: 'Update PBNEF',
          message: `Are you sure you want to update ${this.dbnNumber}'s PBNEF?`,
          buttonLabel: 'Submit',
          buttonColor: '#218100'
        },
        draft: {
          title: 'Save as Draft',
          message: 'You can save the changes and update later.',
          buttonLabel: 'Save',
          buttonColor: '#121F6B'
        },
        discard: {
          title: 'Discard Changes',
          message: 'Changes will not be saved. Are you sure you want to discard?',
          buttonLabel: 'Discard',
          buttonColor: '#A22929'
        },
        endorse: {
          title: 'Endorse PBNEF',
          message: `Are you sure you want to endorse PBNEF for ${this.dbnNumber} to ${this.getEndorseName(this.endorsedTo)}?`,
          buttonLabel: 'Submit',
          buttonColor: '#218100'
        }
      }
    },
    schema () {
      const info = {
        dbnNumber: this.dbnNumber,
        status: this.status,
        isSubmitted: this.isSubmitted,
        docketNumber: this.docketNumber,
        nameOfPic: this.nameOfPic,
        requestFor: this.requestFor,
        specify: this.specify,
        dataSubjects: this.dataSubjects,
        specifyIfOthers: this.specifyIfOthers,
        specifyIfGroup: this.specifyIfGroup,
        numberOfDataSubjectsAffected: this.numberOfDataSubjectsAffected,
        atLeastOneHundredIndividuals: this.atLeastOneHundredIndividuals,
        natureOfPersonalDataInvolved: this.natureOfPersonalDataInvolved,
        measuresInPlace: this.measuresInPlace,
        explanation: this.explanation,
        approveReject: this.approveReject,
        forAdjudication: this.forAdjudication,
        fileUpload: this.fileUpload
      }

      const conditionals = ['endorsedTo', 'evaluatedBy', 'initialReviewedBy', 'finalReviewBy', 'approvedBy', 'evaluation']
      conditionals.forEach(key => {
        if (this[`${key}`] && this[`${key}`] !== '') info[`${key}`] = this[`${key}`]
      })

      return info
    },
    formParams () {
      return {
        query: {},
        fields: {
          fileUpload: this.fileUpload
        }
      }
    },
    isFileEmpty () {
      if (!this.fileUpload) return true
      if (Array.isArray(this.fileUpload) && !this.fileUpload.length) return true
      if (typeof this.fileUpload === 'object' && this.fileUpload.size <= 0) return true

      return false
    },
    fileSize () {
      return this.fileUpload && this.fileUpload.size && this.fileUpload.size !== undefined
        ? this.fileUpload.size : this.fileUpload ? this.fileUpload.reduce((acc, curr) => acc + curr.size, 0) : 0
    }
  },
  watch: {
    operation () {
      this.setMode()
    }
  },
  mounted () {
    window.addEventListener('scroll', this.onScroll)
  },
  async created () {
    this.$meta.loading = true
    if (this.operation !== 'create') {
      await this.view()
    }
    this.setMode()
    this.$meta.loading = false
  },
  beforeDestroy () {
    this.$store.dispatch('pbnef/reset')
  },
  methods: {
    endorseConfirmation () {
      if (!this.isFileEmpty && this.fileSize) {
        this.triggerAlert('You cannot upload a file resolution if you will endorse the form to Adjudication Recipient.')
      } else {
        this.isForEndorsement = true
        this.$v.endorsedTo.$reset()
      }
    },
    scrollToTop () {
      this.$vuetify.goTo(0, 0)
    },
    onScroll () {
      this.showFab = window.top.scrollY > 80
    },
    setMode () {
      this.$meta.readOnly = true
      if (this.operation && this.operation !== 'view') {
        this.$meta.readOnly = false
      }
    },
    api (action) {
      return crud(this.$meta.routes[`${action}`])
    },
    async view () {
      const resp = await this.api('pbnef').read(this.id)
      this.$store.dispatch('pbnef/set', resp.entry)

      if (this.formParams) {
        const image = await this.getImage(this.id, this.fileUpload, {})
        this.fileUpload = image
      }
    },
    getEndorseName (_id) {
      const [endorse] = this.users.filter(x => x._id === _id)
      return endorse && endorse.fullName
    },
    async endorse () {
      this.$v.endorsedTo.$touch()
      if (this.$v.endorsedTo.$invalid) return
      const info = this.dialogInfo.endorse
      const v = this.$refs.form.$v
      info.callback = async () => {
        v.$touch()
        v.fileUpload.$reset()
        if (v.$invalid) return this.triggerAlert('Please validate all the required fields.')
        if (!this.validateChecklist()) {
          this.forAdjudication = true
          await this.save()
          this.isForEndorsement = false
        }
      }
      this.$store.dispatch('showDialog', info)
    },
    async approve () {
      this.approveReject = 'Approved'
      await this.save()
    },
    async reject () {
      this.approveReject = 'Rejected'
      await this.save()
    },
    async save () {
      this.$meta.loading = true
      await this.assignUser()
      await this.setStatus()
      let remarks
      let specify
      if (!(this.natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved') || this.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud'))) {
        remarks = this.natureOfPersonalDataInvolved.remarks
        this.natureOfPersonalDataInvolved.remarks = ''
      }
      if (!this.natureOfPersonalDataInvolved.checklist.includes('Others')) {
        specify = this.natureOfPersonalDataInvolved.specify
        this.natureOfPersonalDataInvolved.specify = ''
      }
      const payloadResp = this.operation === 'create'
        ? await this.api('create').create(this.schema)
        : await this.api('update').update(this.id, this.schema)
      const payloadJson = await payloadResp.json()
      if (payloadResp.ok) {
        if (!this.fileUpload) this.fileUpload = []

        await this.fileUploader(payloadJson)
        if (this.isSubmitted) await this.changePdbnfStatus()

        if (this.isSubmitted) {
          this.$store.dispatch('showAlert', {
            message: `You have successfully ${this.actionStatus} PBNEF for ${this.docketNumber}. `,
            type: 'success'
          })
        } else {
          this.$store.dispatch('showAlert', {
            message: 'Your Preliminary Breach Notification Evaluation Form was successfully saved as draft!',
            type: 'draft'
          })
        }

        const respId = (payloadJson.entry && payloadJson.entry._id) || (payloadJson.updatedResult && payloadJson.updatedResult._id) || ''
        this.$router.push({
          name: 'PBNEFView',
          params: {
            operation: 'view',
            id: respId
          }
        })
      } else {
        this.natureOfPersonalDataInvolved.specify = specify
        this.natureOfPersonalDataInvolved.remarks = remarks
        this.$store.dispatch('showAlert', {
          message: payloadJson.error,
          type: 'error'
        })
      }
      this.$meta.loading = false
      await this.setStatusObj()
      await this.setAssignee()
    },
    async assignUser () {
      if (!this.isSubmitted) return

      if (!this.status || this.status === '' || (this.statusObject && this.statusObject.value === 'Drafted')) { // Evaluator
        this.$store.dispatch('pbnef/set', { evaluatedBy: this.$s.user })
      } else if (this.statusObject.value === 'Initial Review - PBNEF') { // First Reviewer
        this.$store.dispatch('pbnef/set', { initialReviewedBy: this.$s.user })
      } else if (this.statusObject.value === 'Final Review - PBNEF' && !this.withEndorsement) { // Final reviewer approved the pbenf
        this.$store.dispatch('pbnef/set', { finalReviewBy: this.$s.user, approvedBy: this.$s.user })
      } else if (this.statusObject.value === 'Final Review - PBNEF' && this.withEndorsement) { // Final reviewer endorsed the pbnef
        this.$store.dispatch('pbnef/set', { finalReviewBy: this.$s.user })
      } else if (this.statusObject.value === 'For Approval - PBNEF') { // Adjudication recipient approved the pbnef
        this.$store.dispatch('pbnef/set', { approvedBy: this.$s.user })
      }
    },
    async setStatus () {
      let newStatus = ''
      const statusVal = this.dbnStatus.find(x => x._id === this.status)

      if (!this.isSubmitted) {
        newStatus = 'Drafted'
      } else if (statusVal === '' || !statusVal || statusVal.value === 'Drafted') {
        newStatus = 'Initial Review - PBNEF'
        this.actionStatus = 'initiated'
      } else {
        if (statusVal.value === 'Initial Review - PBNEF') {
          newStatus = 'Final Review - PBNEF'
          this.actionStatus = 'evaluated'
        } else if (statusVal.value === 'Final Review - PBNEF' && this.withEndorsement) {
          newStatus = 'For Approval - PBNEF'
          this.actionStatus = 'endorsed'
        } else if (this.approveReject !== '' && (statusVal.value === 'For Approval - PBNEF' || (statusVal.value === 'Final Review - PBNEF' && !this.withEndorsement))) {
          // For Approval - PBNEF = Approved by Adjudication Recipient
          // Final Review - PBNEF w/out endorsement = Approved by final approver
          this.actionStatus = this.approveReject.toLowerCase()

          if (!this.hasApproveReject) newStatus = 'For Evaluation'
          else newStatus = 'Initial Order'
        }
      }

      const dbnStat = this.dbnStatus.find(x => x.value === newStatus)
      dbnStat && this.$store.dispatch('pbnef/set', { status: dbnStat._id })
    },
    confirmApproval (type) {
      this.$refs.form.$v.$touch()
      if (this.$refs.form.$v.$invalid) return this.triggerAlert('Please validate all the required fields.')
      this.$store.dispatch('showDialog', {
        title: `${capitalize(type)} PBNEF`,
        message: `Are you sure you want to ${type} PBNEF for ${this.dbnNumber}?`,
        buttonLabel: `${capitalize(type)}`,
        buttonColor: type === 'approve' ? '#218100' : '#A22929',
        callback: this[`${type}`]
      })
    },
    confirmDialog (type, callback) {
      const isDraft = type === 'draft'
      this.isSubmitted = !isDraft

      if (!isDraft) this.$refs.form.$v.$touch()
      else {
        this.$refs.form.$v.$reset()
        this.$refs.form.$v.requestFor.$touch()
      }

      if (!this.$refs.form.$v.$invalid || isDraft) {
        if (isDraft && this.$refs.form.$v.requestFor.$invalid) {
          return this.$store.dispatch('showAlert', {
            message: 'Please provide the Type of Request.',
            type: 'error'
          })
        }
        const info = this.dialogInfo[`${type}`]
        info.callback = callback
        this.$store.dispatch('showDialog', info)
      } else {
        this.triggerAlert('Please validate all the required fields.')
      }
    },
    triggerAlert (message, type = 'error') {
      this.$store.dispatch('showAlert', {
        message,
        type
      })
    },
    validateChecklist () {
      if (!this.natureOfPersonalDataInvolved.checklist.length) {
        this.triggerAlert('Please select at least one Nature of Personal Data Involved.')
        return true
      }
      if (
        this.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud') &&
          !this.natureOfPersonalDataInvolved.additionalChecklist.length
      ) {
        this.triggerAlert('Please select at least one subdata under the Information that could enable identity fraud')
        return true
      }

      if (!this.evaluation || this.evaluation === '') {
        if (this.natureOfPersonalDataInvolved.checklist.includes('Sensitive Personal Information Involved') ||
         this.natureOfPersonalDataInvolved.checklist.includes('Information that could enable identity fraud')) {
          if (this.natureOfPersonalDataInvolved.remarks) {
            this.triggerAlert('Please select an evaluation.')
            return true
          }
        } else {
          this.triggerAlert('Please select an evaluation.')
          return true
        }
      }

      if (!this.isForEndorsement && this.isAssignee && (this.forFinalReview || this.forApproval) && (this.isFileEmpty || !this.fileSize)) {
        this.triggerAlert('Please Upload File (Resolution).')
        return true
      }

      if (this.fileUpload && ((this.fileSize / 1048576) > this.maxFileSize)) {
        this.triggerAlert(`Total size of file must be ${this.maxFileSize} MB or less.`)
        return true
      }

      return false
    },
    async changePdbnfStatus () {
      // pdbnf status will only change status
      // if all pbnef's from the same pdbnf is with the same status

      const list = await crud('pbnef').list({ advancedQuery: { docketNumber: this.dbnNumber } })
      let submitted = [...list.entries].filter(x => x.isSubmitted)
      let submittedStatus = submitted.reduce((acc, curr) => {
        acc.push(...curr.requestFor)
        return acc
      }, [])
      submittedStatus = [...new Set(submittedStatus)]
      if (submittedStatus.length !== this.pdbnfRequest.length) return
      if (this.hasApproveReject) {
        submitted = submitted.filter(x => {
          if (x.approveReject !== 'Approved' && x.approveReject !== 'Rejected') return x
        })
      }
      const forAppID = this.dbnStatus.find(x => x.value === 'For Approval - PBNEF')
      const forEval = this.dbnStatus.find(x => x.value === 'For Evaluation')
      const forInit = this.dbnStatus.find(x => x.value === 'Initial Order')
      const isFinalReview = forEval._id === this.status || forInit._id === this.status
      let proceed = submitted.every(pbnef => pbnef.status === submitted[0].status) // all pbenf have the same status
      if (isFinalReview && this.hasApproveReject) proceed = submitted.length === 0 // all pbnefs are already approved/rejected
      if (proceed || (forAppID && this.status === forAppID._id)) {
        const resp = await crud(`assign/${this.dataTab}/statusUpdate`).update(this.dbn, {
          status: this.status
        })
        if (forAppID && this.status === forAppID._id) {
          await crud('pdbnf').update(this.dbn, {
            recipient: { adjudication: this.endorsedTo }
          })
        }
        return resp.ok
      } else {
        // revert For Approval status to Final Review - PBNEF after approval of a pbnef
        // if there's still pbnef not yet endorsed or approved

        const forFin = this.dbnStatus.find(x => x.value === 'Final Review - PBNEF')

        if (isFinalReview) {
          const withForApproval = submitted.find(item => forAppID && item.status === forAppID._id)
          if (!withForApproval) {
            const withForReview = submitted.find(item => item.status === forFin._id)
            if (withForReview) {
              await crud(`assign/${this.dataTab}/statusUpdate`).update(this.dbn, {
                status: forFin._id
              })
            }
          }
        }
      }
    }
  },
  validations () {
    return endorseValidation
  }
}
</script>

<style scoped>
.transparent-bg {
  background-color: rgba(0,0,0,0);
}

.v-btn {
  text-transform: none;
}

.btn--fab {
  margin-bottom: 15px;
}
</style>
